// Shared definitions of AI text models and simple env-based toggles

export type AITextProvider = 'deepseek' | 'mistral' | 'openrouter' | 'grok';

// Stable identifiers for text models selectable in the UI
export type AITextModelId =
  | 'deepseek-chat'
  | 'deepseek-reasoner'
  | 'mistral-small-latest'
  | 'ministral-14b-latest'
  | 'openrouter-primary'
  | 'grok-4-1-fast-non-reasoning'
  | 'grok-4-1-fast-reasoning';

export interface AITextModelOption {
  id: AITextModelId;
  label: string;
  provider: AITextProvider;
  description?: string;
}

const ALL_TEXT_MODELS: AITextModelOption[] = [
  {
    id: 'deepseek-chat',
    label: 'DeepSeek Chat',
    provider: 'deepseek',
    description: 'Rychlý univerzální model pro textové úlohy.',
  },
  {
    id: 'deepseek-reasoner',
    label: 'DeepSeek Reasoner',
    provider: 'deepseek',
    description: 'Model zaměřený na hlubší vysvětlování a řetězení úvah.',
  },
  {
    id: 'mistral-small-latest',
    label: 'Mistral Small',
    provider: 'mistral',
    description: 'Lehčí Mistral pro rychlejší odpovědi.',
  },
  {
    id: 'ministral-14b-latest',
    label: 'Ministral 14B',
    provider: 'mistral',
    description: 'Výkonnější Mistral 14B pro kvalitnější text.',
  },
  {
    id: 'openrouter-primary',
    label: 'OpenRouter (výchozí)',
    provider: 'openrouter',
    description: 'Konfigurace OpenRouter dle backendu.',
  },
  {
    id: 'grok-4-1-fast-non-reasoning',
    label: 'Grok 4.1 Fast',
    provider: 'grok',
    description: 'Rychlý model od xAI pro běžné úlohy.',
  },
  {
    id: 'grok-4-1-fast-reasoning',
    label: 'Grok 4.1 Fast Reasoning',
    provider: 'grok',
    description: 'Model s reasoning pro hlubší analýzu (může trvat déle).',
  },
];

function parseBoolEnv(name: string): boolean {
  const raw = (process.env[name] || '').toString().trim().toLowerCase();
  if (!raw) return false;
  return raw === '1' || raw === 'true' || raw === 'yes';
}

/**
 * Vrátí textové modely povolené pro výběr v UI.
 *
 * Pokud je v prostředí zapnutý OpenRouter mód (REACT_APP_OPENROUTER_ON),
 * zobrazí se pouze OpenRouter model. Jinak jsou dostupné DeepSeek + Mistral + Grok.
 */
export function getEnabledTextModels(): AITextModelOption[] {
  try {
    const openrouterOn = parseBoolEnv('REACT_APP_OPENROUTER_ON');

    if (openrouterOn) {
      return ALL_TEXT_MODELS.filter((m) => m.provider === 'openrouter');
    }

    return ALL_TEXT_MODELS.filter((m) => m.provider !== 'openrouter');
  } catch (error) {
    // Fallback in case of any errors during initialization
    return ALL_TEXT_MODELS.filter((m) => m.provider === 'deepseek' || m.provider === 'mistral');
  }
}

export function getDefaultTextModelId(): AITextModelId {
  try {
    const models = getEnabledTextModels();
    return models[0]?.id ?? 'deepseek-chat';
  } catch (error) {
    // Ultimate fallback
    return 'deepseek-chat';
  }
}

export function findTextModel(id: AITextModelId): AITextModelOption | undefined {
  return ALL_TEXT_MODELS.find((m) => m.id === id);
}
